import { ChangeDetectionStrategy, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { TabView, TabViewModule } from 'primeng/tabview';
import { StandardTranscriptComponent } from './components/standard-transcript/standard-transcript.component';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { ITranscriptionState, TRANSCRIPTION_REDUCER } from '../../store/reducers/transcription.reducer';
import { AlertComponent } from '../../shared/components/alert/alert.component';
import { AudioPlayerComponent } from './components/audio-player/audio-player.component';
import { AceTranscriptComponent } from './components/ace-transcript/ace-transcript.component';
import { TranscriptionDateComponent } from './components/transcription-date/transcription-date.component';
import { EHttpErrorStatus } from '../../core/configurations/errors';
import { CONVERSATION_DATA_REDUCER } from '../../store/reducers/conversation-data.reducer';
import { ConversationHeaderComponent } from './components/conversation-header/conversation-header.component';
import { APPLICATION_REDUCER } from '../../store/reducers/application.reducer';
import { EScreenSizeBreakPoint } from '../../core/configurations/application';
import { SkeletonModule } from 'primeng/skeleton';
import { IntersectionDirective } from '../../shared/directives/intersection.directive';
import {
  EChannel,
  IStandardParagraphNormalized,
  IStandardTranscriptNormalized,
} from '../../shared/models/standard-transcript-normalized.model';
import { MessageService } from 'primeng/api';
import { IAceTranscriptAPIResponse } from '../../shared/interfaces/ace-transcript.interface';
import { HasTranscriptionPipe } from './pipes/has-transcription.pipe';
import { PdfContentSelectionDialogComponent } from './components/pdf-content-selection-dialog/pdf-content-selection-dialog.component';

const TRANSCRIPT_ERROR_MESSAGE =
  'Oops! We encountered a problem retrieving the transcript. Please check your internet connection and try again.\n' +
  'If the problem persists, the file may be temporarily unavailable or the link might be broken. For further\n' +
  'assistance, please contact our support team.';
const AUDIO_ERROR_MESSAGE =
  'Oops! We encountered a problem retrieving the audio file. Please check your internet connection and try again.\n' +
  'If the problem persists, the file may be temporarily unavailable or the link might be broken. For further\n' +
  'assistance, please contact our support team.';

@Component({
  selector: 'cv-transcriptions',
  standalone: true,
  imports: [
    TabViewModule,
    StandardTranscriptComponent,
    AsyncPipe,
    AlertComponent,
    AudioPlayerComponent,
    AceTranscriptComponent,
    TranscriptionDateComponent,
    ConversationHeaderComponent,
    SkeletonModule,
    IntersectionDirective,
    HasTranscriptionPipe,
    PdfContentSelectionDialogComponent,
  ],
  templateUrl: './transcriptions.component.html',
  styleUrl: './transcriptions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TranscriptionsComponent {
  @ViewChild('tabView') tabView!: TabView;
  @ViewChild('audioPlayer') audioPlayer!: AudioPlayerComponent;

  readonly transcriptErrormessage = TRANSCRIPT_ERROR_MESSAGE;
  readonly audioErrormessage = AUDIO_ERROR_MESSAGE;
  readonly tabEnum = ETabHeader;
  readonly EHttpErrorStatus = EHttpErrorStatus;
  readonly data$: Observable<ITranscriptionComponentData> = combineLatest({
    transcription: this.store.select<ITranscriptionState>(TRANSCRIPTION_REDUCER.selectTranscriptionState),
    userName: this.store.select<string>(CONVERSATION_DATA_REDUCER.userName),
    agentName: of('Agent'),
    isMobileScreenSize: this.store.select(APPLICATION_REDUCER.isScreenSizeBreakpoint(EScreenSizeBreakPoint.xs)),
  });
  currentAudioTime: number = 0;
  isShowPdfContentSelectionDialog = false;

  constructor(
    private readonly store: Store,
    private readonly toastService: MessageService,
  ) {}

  //TODO: consider creating service if copy functionality is expanding
  async onCopyTranscript(): Promise<void> {
    const { agentName, transcription, userName } = await firstValueFrom(this.data$);
    let transcriptionAsText: string;

    if (this.tabView.findSelectedTab()?.header === ETabHeader.standard) {
      transcriptionAsText = this.getStandardTranscriptionAsText(
        agentName,
        userName,
        transcription.standardTranscription.data,
      );
    } else {
      transcriptionAsText = this.getAceTranscriptionAsText(agentName, userName, transcription.aceTranscription.data);
    }

    void this.copyToClipboard(transcriptionAsText, 'Transcription copied to clipboard');
  }

  onDownloadCallAudio(audioSource: string | null): void {
    if (!audioSource) {
      return;
    }
    const link = document.createElement('a');
    link.href = audioSource;
    link.download = 'call-audio.mp3';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getAceTranscriptionAsText(
    agentName: string,
    userName: string,
    transcription: IAceTranscriptAPIResponse | null,
  ): string {
    if (!Array.isArray(transcription?.paragraphs)) {
      return '';
    }
    return transcription.paragraphs
      .map(({ paragraph, channel }) => `${channel === EChannel.agent ? agentName : userName}: ${paragraph}\n`)
      .join('');
  }

  private getStandardTranscriptionAsText(
    agentName: string,
    userName: string,
    transcription: IStandardTranscriptNormalized | null,
  ): string {
    if (!Array.isArray(transcription?.paragraphs)) {
      return '';
    }

    return transcription.paragraphs.reduce((acc: string, { words, channel }: IStandardParagraphNormalized): string => {
      const owner = channel === EChannel.agent ? agentName : userName;
      const message = words.map(({ punctuatedWord }) => punctuatedWord).join(' ');

      return acc.concat(`${owner}: ${message}\n`);
    }, '');
  }

  private async copyToClipboard(textToCopy: string, successMessage: string): Promise<void> {
    await navigator.clipboard.writeText(textToCopy);

    this.toastService.add({
      severity: 'success',
      summary: 'Copied',
      detail: successMessage,
    });
  }
}

export enum ETabHeader {
  ace = 'ACE',
  standard = 'Standard',
}

interface ITranscriptionComponentData {
  transcription: ITranscriptionState;
  userName: string;
  agentName: string;
  isMobileScreenSize: boolean;
}
